import config from 'constants/config'
import type { PaymentMethod } from 'constants/payment'
import {
  KLARNA_PAYMENT_TYPE,
  KLARNA_BP_PAYMENT_TYPE,
  MIN_KLARNA_PAYMENT,
  MAX_KLARNA_PAYMENT,
  MIN_KLARNA_BP_GBP_PAYMENT,
  MIN_KLARNA_BP_NZD_PAYMENT,
  MIN_KLARNA_BP_USD_PAYMENT,
  MAX_KLARNA_BP_GBP_PAYMENT,
  MAX_KLARNA_BP_NZD_PAYMENT,
  MAX_KLARNA_BP_USD_PAYMENT,
  BRAINTREE_PAYMENT_TYPE,
  APPLE_PAY_PAYMENT_TYPE,
  BASE_MERCHANT_FEE,
  GOOGLE_PAY_PAYMENT_TYPE,
  GO_CARDLESS_PAYMENT_TYPE,
  STRIPE_CUSTOM_PAYTO_TYPE,
  STRIPE_PAYMENT_ELEMENT_CARD_TYPE,
  STRIPE_PAYMENT_ELEMENT_KLARNA_TYPE,
  SECURE_PAYMENT_TYPE,
  SHORT_LEAD_TIME_WITH_CREDIT_MODAL_LEAD_TIME_DAYS,
  SHORT_LEAD_TIME_WITH_CREDIT_MODAL_PERCENTAGE_THRESHOLD,
  PAYPAL_PAYMENT_TYPE, PAYLATER_PAYMENT_TYPE,
  FLYING_BLUE_PAYMENT_TYPE,
  STRIPE_PAYMENT_TYPE,
  RAZORPAY_PAYMENT_TYPE,
  CREDIT_PAYMENT_TYPE,
  DEBIT_CARD_MERCHANT_FEE,
  STRIPE_PAYMENT_ELEMENT_AFTERPAY_TYPE,
  STRIPE_PAYMENT_ELEMENT_GIROPAY_TYPE,
  STRIPE_PAYMENT_ELEMENT_IDEAL_TYPE,
  STRIPE_PAYMENT_ELEMENT_PAYNOW_TYPE,
  STRIPE_PAYMENT_ELEMENT_GRABPAY_TYPE,
  STRIPE_PAYMENT_ELEMENT_WECHATPAY_TYPE,
  VELOCITY_PAYMENT_TYPE,
  AZUPAY_PAY_ID_PAYMENT_TYPE,
  STRIPE_TERMINAL_PAYMENT_TYPE,
} from 'constants/payment'
import getPaymentMethods from 'lib/payment/getPaymentMethods'
import { isAfterPayBpEnabled, isWithinAfterPayBpAvailabilityRange } from 'lib/payment/afterPayBpUtils'
import { isHoolahBpEnabled, isWithinHoolahBpAvailabilityRange } from 'lib/payment/hoolahBpUtils'
import { isAtomeBpEnabled, isWithinAtomeBpAvailabilityRange } from 'lib/payment/atomeBpUtils'
import { isWaaveBpEnabled, isWithinWaaveBpAvailabilityRange } from 'lib/payment/waaveBpUtils'
import { isEnabledForFeature } from 'lib/config/featureFlagUtils'
import type { PaymentMethodRowData } from 'checkout/Components/CheckoutSteps/Payment/PurchaseBox/PaymentOptions/common'
import moment from 'moment'
import { calculateDaysBetweenDates } from './payInInstalmentsUtils'
import { sum } from 'lib/array/arrayUtils'
import { isBedbankItem, isCarHireItem, isCruiseItem, isExperienceItem, isFlightItem, isInstantBookingLEHotelItem, isTourItem, isTourV2ExperienceItem, isTransferItem, isVillaItem } from 'lib/checkout/checkoutUtils'
import type { IntlShape, MessageDescriptor } from 'react-intl'
import { defineMessages } from 'react-intl'

export const isKlarnaEnabled = (currency: string): boolean =>
  !!(config.KLARNA_ENABLED && getPaymentMethods(currency).includes(KLARNA_PAYMENT_TYPE))

export const isWithinKlarnaAvailabilityRange = (amount: number): boolean =>
  amount >= MIN_KLARNA_PAYMENT && amount <= MAX_KLARNA_PAYMENT

const paymentMethodMessages = defineMessages({
  applePay: { id: 'vqxYmFIclluyOkC0kquG', defaultMessage: 'Apple Pay', description: 'Payment method label for Apple Pay on the checkout page' },
  googlePay: { id: 'CsaKzJEdsZI5jpaaSMwV', defaultMessage: 'Google Pay', description: 'Payment method label for Google Pay on the checkout page' },
  stripeElement: { id: 'WISmjKyFPPioWpB2FBm3', defaultMessage: 'Stripe Payment Element', description: 'Payment method label for Stripe Payment Element on the checkout page' },
  creditOrDebit: { id: 'Up1Z21HYYKbHKtv_xnk4', defaultMessage: 'Credit or debit card', description: 'Payment method label for credit or debit card on the checkout page' },
  paypal: { id: 'g1PO5mbvMqyNKqNem1Cw', defaultMessage: 'PayPal', description: 'Payment method label for PayPal on the checkout page' },
  velocityPoints: { id: 'nCQP74ZidDb4gDr3j_t7', defaultMessage: 'Velocity Points', description: 'Payment method label for Velocity Points on the checkout page' },
  klarna: { id: 'NtZHTJLdDtB12UJbnNm8', defaultMessage: 'Klarna', description: 'Payment method label for Klarna on the checkout page' },
  klarnaBp: { id: 'BgtSMa_PFS5VsOh05Xkt', defaultMessage: 'Klarna', description: 'Payment method label for Klarna buy-now-pay-later on the checkout page' },
  afterpay: { id: '5kTXW_Aisq_XyG-2GhAd', defaultMessage: 'Afterpay', description: 'Payment method label for Afterpay on the checkout page' },
  hoolah: { id: '6dJXWw4w1E8Ud2MSOYkf', defaultMessage: 'Hoolah', description: 'Payment method label for Hoolah on the checkout page' },
  atome: { id: 'RJG0hsGcEZuGq-hKy6Rt', defaultMessage: 'Atome', description: 'Payment method label for Atome on the checkout page' },
  waave: { id: 'uuKVCbp-nza-uhCWE2FQ', defaultMessage: 'Waave', description: 'Payment method label for Waave on the checkout page' },
  razorpay: { id: '50Api_Ta0lCa-f0qVmEE', defaultMessage: 'Razorpay', description: 'Payment method label for Razorpay on the checkout page' },
  payTo: { id: 'u_Uh6qmNCLZTWiSiG9G7', defaultMessage: 'PayTo', description: 'Payment method label for PayTo on the checkout page' },
  flyingBlue: { id: 'cLetBswdvto1LVsKYNeX', defaultMessage: 'Flyingblue', description: 'Payment method label for Flying Blue on the checkout page' },
  payId: { id: '4twNkHZpoPC2IEW6CGq1', defaultMessage: 'PayID', description: 'Payment method label for PayID on the checkout page' },
  stripeTerminal: { id: 'kviPkChdzNidhIHNNusv', defaultMessage: 'Stripe Terminal', description: 'Payment method label for Stripe Terminal on the checkout page' },
})

const messages = defineMessages({
  promoCodeAppliesTo: {
    id: 'x0yUeTpT6ZS_UFH8BTxb',
    defaultMessage: 'This promo code applies to {paymentMethods}',
    description: 'Promo code applicability text listing supported payment methods',
  },
  paymentFeePercent: {
    id: 'x1ZuwXuyMgwsxFxZ9eHd',
    defaultMessage: 'Payment fee of {baseMerchantFee}%',
    description: 'Payment fee caption showing a single percentage fee',
  },
  payLaterPayIn4Description: {
    id: '3FwAMWuDM6m9uS02ltk0',
    defaultMessage: 'No interest. No late fees. Payment fee of {baseMerchantFee}%',
    description: 'Description shown for PayPal Pay in 4 in checkout payment method row with explicit fee details',
  },
  noPaymentFeeBankTransfer: {
    id: 'INCCRU9GgfGfiOcM7cgv',
    defaultMessage: 'No Payment fee; transfer directly from your bank account',
    description: 'Payment fee caption for bank transfer methods with no fee',
  },
  noPaymentFeePayTo: {
    id: 'vvhmzM6xD1j4SLB_WhBe',
    defaultMessage: 'No Payment fee; Pay quickly and directly from your bank account with PayTo',
    description: 'Payment fee caption for PayTo with no fee in account settings',
  },
  debitAndCreditCardFeesFrom: {
    id: 'arUxCbPhmFDYJ0OpDmxf',
    defaultMessage: 'Debit card fees from {debitFee}%, Credit card fees from {baseFee}%',
    description: 'Payment fee caption for debit and credit card without wallet availability',
  },
  debitAndCreditCardFeesFromWithGooglePay: {
    id: 'eV-klieoEnmvoL2b3N1m',
    defaultMessage: 'Debit card fees from {debitFee}%, Credit card and Google Pay fees from {baseFee}%',
    description: 'Payment fee caption for debit and credit card with Google Pay',
  },
  debitAndCreditCardFeesFromWithApplePay: {
    id: 'bLi5F7HYzUYFZJUCkGge',
    defaultMessage: 'Debit card fees from {debitFee}%, Credit card and Apple Pay fees from {baseFee}%',
    description: 'Payment fee caption for debit and credit card with Apple Pay',
  },
  noDebitCardFeeCreditCardFeeFrom: {
    id: 'H19UdXKDVMBxPJcEQs4b',
    defaultMessage: 'No Debit card fee, Credit card fees from {baseFee}%',
    description: 'Payment fee caption where debit card has no fee and credit card has fee without wallet availability',
  },
  noDebitCardFeeCreditCardFeeFromWithGooglePay: {
    id: 'Eg2bfKGzRP5T3zkPJ02V',
    defaultMessage: 'No Debit card fee, Credit card and Google Pay fees from {baseFee}%',
    description: 'Payment fee caption where debit card has no fee and credit card has fee with Google Pay',
  },
  noDebitCardFeeCreditCardFeeFromWithApplePay: {
    id: 'IuWptcPQ_OxSF-miJKuD',
    defaultMessage: 'No Debit card fee, Credit card and Apple Pay fees from {baseFee}%',
    description: 'Payment fee caption where debit card has no fee and credit card has fee with Apple Pay',
  },
  generatePaymentLinkSecurely: {
    id: 'YNE56fYZZtO6F5DPza9r',
    defaultMessage: 'Generate a payment link to receive payment securely over the phone',
    description: 'Payment fee caption for secure payment links in the payment section',
  },
})

const formatMessage = (
  intl: IntlShape,
  descriptor: MessageDescriptor,
  values?: Record<string, string | number>,
) => intl.formatMessage(descriptor, values)

export const isKlarnaBpEnabled = (currency: string): boolean => {
  return config.KLARNA_BP_CURRENCIES ? isEnabledForFeature(config.KLARNA_BP_CURRENCIES, currency) && getPaymentMethods(currency).includes(KLARNA_BP_PAYMENT_TYPE) : false
}

export const isWithinKlarnaBpAvailabilityRange = (amount: number, currency: string): boolean => {
  switch (currency) {
    case 'NZD':
      return (amount >= MIN_KLARNA_BP_NZD_PAYMENT && amount <= MAX_KLARNA_BP_NZD_PAYMENT)
    case 'USD':
      return (amount >= MIN_KLARNA_BP_USD_PAYMENT && amount <= MAX_KLARNA_BP_USD_PAYMENT)
    case 'GBP':
      return (amount >= MIN_KLARNA_BP_GBP_PAYMENT && amount <= MAX_KLARNA_BP_GBP_PAYMENT)
    default:
      return false
  }
}

export const getKlarnaBpMaxLimit = (currency: string): number => {
  switch (currency) {
    case 'NZD':
      return MAX_KLARNA_BP_NZD_PAYMENT
    case 'USD':
      return MAX_KLARNA_BP_USD_PAYMENT
    case 'GBP':
      return MAX_KLARNA_BP_GBP_PAYMENT
    default:
      return 0
  }
}

export function calculateInstalments(amount: number, instalments: number): number {
  return Math.ceil(parseFloat(((amount / instalments) * 100).toFixed(3))) / 100
}

export function isBraintreeEnabled(currency: string) {
  return getPaymentMethods(currency).includes(BRAINTREE_PAYMENT_TYPE)
}

export function isPayPalEnabled(currency: string) {
  return config.PAYPAL_ENABLED && getPaymentMethods(currency).includes(PAYPAL_PAYMENT_TYPE)
}

export function getBNPLMethodAvailability(currencyCode: string, ...prices: Array<number>) {
  return {
    isKlarnaAvailable: isKlarnaEnabled(currencyCode) && prices.some(price => isWithinKlarnaAvailabilityRange(price)),
    isKlarnaBpAvailable: isKlarnaBpEnabled(currencyCode) && prices.some(price => isWithinKlarnaBpAvailabilityRange(price, currencyCode)),
    isAfterPayBpAvailable: isAfterPayBpEnabled(currencyCode) && prices.some(price => isWithinAfterPayBpAvailabilityRange(price, currencyCode)),
    isHoolahBpAvailable: isHoolahBpEnabled(currencyCode) && prices.some(price => isWithinHoolahBpAvailabilityRange(price, currencyCode)),
    isAtomeBpAvailable: isAtomeBpEnabled(currencyCode) && prices.some(price => isWithinAtomeBpAvailabilityRange(price, currencyCode)),
    isWaaveBpAvailable: isWaaveBpEnabled(currencyCode) && prices.some(price => isWithinWaaveBpAvailabilityRange(price, currencyCode)),
  }
}

export function isWithInStartDateRange(startDate: string, eligibilityRangeInDays: number) {
  if (!startDate) return true

  const currentDate = Date.now()
  const eligibilityRange = eligibilityRangeInDays * 24 * 60 * 60 * 1000

  const startDateInIsoFormat = new Date(startDate).toISOString()
  const startDateInMiliseonds = Date.parse(startDateInIsoFormat)

  return currentDate + eligibilityRange < startDateInMiliseonds
}

export function paymentMethodsToString(paymentMethods: Array<string> = [], intl: IntlShape): string {
  const localizedLabels: Record<string, string> = {
    [APPLE_PAY_PAYMENT_TYPE]: intl.formatMessage(paymentMethodMessages.applePay),
    [GOOGLE_PAY_PAYMENT_TYPE]: intl.formatMessage(paymentMethodMessages.googlePay),
    [STRIPE_PAYMENT_ELEMENT_CARD_TYPE]: intl.formatMessage(paymentMethodMessages.creditOrDebit),
    [BRAINTREE_PAYMENT_TYPE]: intl.formatMessage(paymentMethodMessages.paypal),
    [VELOCITY_PAYMENT_TYPE]: intl.formatMessage(paymentMethodMessages.velocityPoints),
    [KLARNA_PAYMENT_TYPE]: intl.formatMessage(paymentMethodMessages.klarna),
    [KLARNA_BP_PAYMENT_TYPE]: intl.formatMessage(paymentMethodMessages.klarnaBp),
    [RAZORPAY_PAYMENT_TYPE]: intl.formatMessage(paymentMethodMessages.razorpay),
    [GO_CARDLESS_PAYMENT_TYPE]: intl.formatMessage(paymentMethodMessages.payTo),
    [FLYING_BLUE_PAYMENT_TYPE]: intl.formatMessage(paymentMethodMessages.flyingBlue),
    [AZUPAY_PAY_ID_PAYMENT_TYPE]: intl.formatMessage(paymentMethodMessages.payId),
    [STRIPE_TERMINAL_PAYMENT_TYPE]: intl.formatMessage(paymentMethodMessages.stripeTerminal),
  }
  const s = new Set<string>()
  paymentMethods.forEach(method => s.add(localizedLabels[method] || method))
  const arr = Array.from(s)
  const formatter = new Intl.ListFormat(intl.locale || 'en', { type: 'disjunction' })
  return formatMessage(intl, messages.promoCodeAppliesTo, {
    paymentMethods: formatter.format(arr),
  })
}

const merchantFeePaymentTypes: Array<App.MerchantFeePaymentType> = ['visa', 'mastercard', 'amex']
export function isCardAcceptedMerchantFeePaymentType(brand: string): brand is App.MerchantFeePaymentType {
  return merchantFeePaymentTypes.includes(brand as App.MerchantFeePaymentType)
}

export function getMerchantFeeByOrderItem(merchantFeeDetails: Array<App.MerchantFeeDetails>, itemId: string): App.MerchantFeeDetails | undefined {
  return merchantFeeDetails?.find(item => item.orderItemId === itemId)
}

type WalletAvailability = 'google' | 'apple' | 'none'

function getWalletAvailability(
  isGooglePayAvailable: boolean,
  isApplePayAvailable: boolean,
): WalletAvailability {
  if (isGooglePayAvailable) return 'google'
  if (isApplePayAvailable) return 'apple'
  return 'none'
}

function getDebitAndCreditCardFeeMessage(
  wallet: WalletAvailability,
  intl: IntlShape,
): string {
  const feeValues = { debitFee: DEBIT_CARD_MERCHANT_FEE, baseFee: BASE_MERCHANT_FEE }
  switch (wallet) {
    case 'google':
      return formatMessage(intl, messages.debitAndCreditCardFeesFromWithGooglePay, feeValues)
    case 'apple':
      return formatMessage(intl, messages.debitAndCreditCardFeesFromWithApplePay, feeValues)
    default:
      return formatMessage(intl, messages.debitAndCreditCardFeesFrom, feeValues)
  }
}

function getNoDebitCardFeeMessage(
  wallet: WalletAvailability,
  intl: IntlShape,
): string {
  const feeValues = { baseFee: BASE_MERCHANT_FEE }
  switch (wallet) {
    case 'google':
      return formatMessage(intl, messages.noDebitCardFeeCreditCardFeeFromWithGooglePay, feeValues)
    case 'apple':
      return formatMessage(intl, messages.noDebitCardFeeCreditCardFeeFromWithApplePay, feeValues)
    default:
      return formatMessage(intl, messages.noDebitCardFeeCreditCardFeeFrom, feeValues)
  }
}

function merchantFeeCaption(
  pspName: string,
  isGooglePayAvailable: boolean,
  isApplePayAvailable: boolean,
  isDebitCardMerchantFeesEnabled: boolean,
  intl: IntlShape,
) {
  const wallet = getWalletAvailability(isGooglePayAvailable, isApplePayAvailable)

  switch (pspName) {
    case BRAINTREE_PAYMENT_TYPE:
    case PAYPAL_PAYMENT_TYPE:
    case APPLE_PAY_PAYMENT_TYPE:
    case GOOGLE_PAY_PAYMENT_TYPE:
    case STRIPE_PAYMENT_ELEMENT_KLARNA_TYPE:
      return formatMessage(intl, messages.paymentFeePercent, {
        baseMerchantFee: BASE_MERCHANT_FEE,
      })
    case PAYLATER_PAYMENT_TYPE:
      return formatMessage(intl, messages.payLaterPayIn4Description, {
        baseMerchantFee: BASE_MERCHANT_FEE,
      })
    case GO_CARDLESS_PAYMENT_TYPE:
      return formatMessage(intl, messages.noPaymentFeeBankTransfer)
    case STRIPE_CUSTOM_PAYTO_TYPE:
      return formatMessage(intl, messages.noPaymentFeePayTo)
    case STRIPE_PAYMENT_ELEMENT_CARD_TYPE:
      if (isDebitCardMerchantFeesEnabled) {
        return getDebitAndCreditCardFeeMessage(wallet, intl)
      }
      return getNoDebitCardFeeMessage(wallet, intl)
    case SECURE_PAYMENT_TYPE:
      return formatMessage(intl, messages.generatePaymentLinkSecurely)
    default:
      return null
  }
}

export function editPaymentMethodCaptions(
  paymentMethods: Array<PaymentMethodRowData>,
  isMerchantFeeAvailable: boolean,
  isStandaloneLuxPlusSubscription: boolean,
  isGooglePayAvailable: boolean,
  isApplePayAvailable: boolean,
  isDebitCardMerchantFeesEnabled: boolean = false,
  intl: IntlShape,
): Array<PaymentMethodRowData> {
  // No Merchant fees on LuxPLus
  if (!isMerchantFeeAvailable || isStandaloneLuxPlusSubscription) return paymentMethods

  const paymentMethodsWithUpdatedLabels = paymentMethods.map((paymentMethod) => {
    return {
      ...paymentMethod,
      subLabel: (
        merchantFeeCaption(
          paymentMethod.name,
          isGooglePayAvailable,
          isApplePayAvailable,
          isDebitCardMerchantFeesEnabled,
          intl,
        ) || paymentMethod.subLabel
      ),
    }
  })

  return paymentMethodsWithUpdatedLabels
}

export function isStripePaymentCardV2(card: App.StripePaymentCard | App.StripePaymentCardV2): card is App.StripePaymentCardV2 {
  return 'funding' in card
}

export function shouldBlockShortLeadTimeWithCredit(payments: Array<App.PaymentInfo>, projectedLeadTimeDays: number): boolean {
  const creditAmount = sum(payments?.filter(payment => payment.type === 'credits') ?? [], (payment) => payment.amount)
  const orderAmount = sum(payments ?? [], (payment) => payment.amount)

  return config.FRAUD_SHORT_LEADTIME_PLUS_CREDITS_BLOCK_ENABLED &&
    !!orderAmount && // when order modification is not free for post purchase
    projectedLeadTimeDays <= SHORT_LEAD_TIME_WITH_CREDIT_MODAL_LEAD_TIME_DAYS &&
    (creditAmount / orderAmount) > SHORT_LEAD_TIME_WITH_CREDIT_MODAL_PERCENTAGE_THRESHOLD
}

export function getProjectedLeadTimeDaysByCartItems(cartItems: Array<App.Checkout.AnyItem>): number {
  const DEFAULT_LEAD_TIME = 999
  const leadTimeList = cartItems.map(cartItem => {
    if (
      isInstantBookingLEHotelItem(cartItem) || // Covers LPC and Flash
      isBedbankItem(cartItem) || // Covers LPP
      isVillaItem(cartItem)
    ) {
      return cartItem.checkIn
    }

    if (
      isCruiseItem(cartItem) ||
      isTourItem(cartItem)
    ) {
      return cartItem.startDate
    }

    if (
      isExperienceItem(cartItem) ||
      isTourV2ExperienceItem(cartItem)
    ) {
      return cartItem.date
    }

    if (
      isFlightItem(cartItem)
    ) {
      return cartItem.flights?.[0]?.departingDate ?? DEFAULT_LEAD_TIME
    }

    if (
      isCarHireItem(cartItem)
    ) {
      return cartItem.pickUpDate
    }

    if (
      isTransferItem(cartItem)
    ) {
      return cartItem.transfer.date
    }

    // Item does not appear to have a date so should assign date with default lead time.
    const today = moment.utc().format('YYYY-MM-DD')
    return moment(today).add(DEFAULT_LEAD_TIME, 'days').endOf('day').format('YYYY-MM-DD')
  }).filter(date => typeof date === 'string' && date).sort() as Array<string>

  if (!leadTimeList.length) {
    return DEFAULT_LEAD_TIME
  }

  return calculateDaysBetweenDates(leadTimeList[0])
}

export const paymentElementMethodMap: Utils.PartialRecord<PaymentMethod, string> = {
  [STRIPE_PAYMENT_ELEMENT_CARD_TYPE]: 'card',
  [STRIPE_PAYMENT_ELEMENT_AFTERPAY_TYPE]: 'afterpay_clearpay',
  [STRIPE_PAYMENT_ELEMENT_GIROPAY_TYPE]: 'giropay',
  [STRIPE_PAYMENT_ELEMENT_IDEAL_TYPE]: 'ideal',
  [STRIPE_PAYMENT_ELEMENT_KLARNA_TYPE]: 'klarna',
  [STRIPE_PAYMENT_ELEMENT_PAYNOW_TYPE]: 'paynow',
  [STRIPE_PAYMENT_ELEMENT_GRABPAY_TYPE]: 'grabpay',
  [STRIPE_PAYMENT_ELEMENT_WECHATPAY_TYPE]: 'wechat_pay',
  [STRIPE_CUSTOM_PAYTO_TYPE]: 'payto',
  [PAYPAL_PAYMENT_TYPE]: 'paypal',
  [PAYLATER_PAYMENT_TYPE]: 'paylater',
  [APPLE_PAY_PAYMENT_TYPE]: 'applepay',
  [GOOGLE_PAY_PAYMENT_TYPE]: 'googlepay',
  [VELOCITY_PAYMENT_TYPE]: 'velocity',
  [AZUPAY_PAY_ID_PAYMENT_TYPE]: 'azupay_pay_id',
  [FLYING_BLUE_PAYMENT_TYPE]: 'flying_blue',
  [GO_CARDLESS_PAYMENT_TYPE]: 'payto',
  [BRAINTREE_PAYMENT_TYPE]: 'card',
  [STRIPE_PAYMENT_TYPE]: 'card',
  [RAZORPAY_PAYMENT_TYPE]: 'card',
  [CREDIT_PAYMENT_TYPE]: 'credits',
  [SECURE_PAYMENT_TYPE]: 'card',
  [STRIPE_TERMINAL_PAYMENT_TYPE]: 'card_present',
}

export const getMappedPaymentMethodForLogging = (
  paymentMethod: PaymentMethod | string | null | undefined,
): string => {
  if (!paymentMethod) {
    return 'unknown'
  }

  return paymentElementMethodMap[paymentMethod as PaymentMethod] ?? 'unknown'
}
